import { signal } from "@preact/signals";

import * as deckSelection from "deckSelection";
import type PracticeStore from "stores/PracticeStore";
import type UserWordStore from "stores/UserWordStore";

export default class FlashcardSessionStore {
  readonly currentTestDeck = signal<UserWordStore[]>([]);
  readonly currentDeckSize = signal<number>(0);
  readonly currentWord = signal<UserWordStore | null>(null);

  constructor(
    private practiceStore: Pick<PracticeStore, "remove" | "reportRecallEase">
  ) {}

  reset() {
    this.currentTestDeck.value = [];
    this.currentDeckSize.value = 0;
    this.currentWord.value = null;
  }

  start(
    readyToTest: UserWordStore[],
    numberOfCards: number,
    mixedMode: boolean
  ) {
    const deck = deckSelection.selectDeckWords({
      candidates: readyToTest,
      sessionSize: numberOfCards,
    });

    if (mixedMode) {
      for (const userWord of deck) {
        userWord.patch({ passiveOverride: Math.random() < 0.5 });
      }
    }

    this.setDeck(deck);
  }

  setDeck(deck: UserWordStore[]) {
    this.currentTestDeck.value = deck;
    this.currentDeckSize.value = deck.length;
  }

  setCurrentWordFromDeck() {
    this.currentWord.value = this.currentTestDeck.value[0] ?? null;
  }

  deleteCurrentWord() {
    const currentWord = this.currentWord.value;
    if (!currentWord) return;

    this.currentTestDeck.value = this.currentTestDeck.value.filter(
      (word) => word.id !== currentWord.id
    );
    this.practiceStore.remove(currentWord);
    currentWord.deleteUserWord({ success: function () {} });
  }

  async reportRecallEase(ease: number, difficulty = 1) {
    const currentWord = this.currentWord.value;
    if (!currentWord) return;

    this.currentTestDeck.value = this.currentTestDeck.value.slice(1);

    const toWait = await this.practiceStore.reportRecallEase(
      currentWord,
      ease,
      difficulty,
      "classic"
    );

    currentWord.patch({ passiveOverride: false }); // only needed for the first exposure to the word

    if (toWait > 0) {
      this.currentTestDeck.value = [...this.currentTestDeck.value, currentWord];
    }
  }

  /** @returns {boolean} true if a session is currently in progress */
  inProgress(): boolean {
    const currentTestDeck = this.currentTestDeck.value;
    return currentTestDeck ? currentTestDeck.length > 0 : false;
  }
}
